import type { JobDto, OverviewResponse, RerateResult } from '@zinc/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { NightlyLines } from './AdminSolverReportPage';
import { Bar, Panel, SmallSwitch, smallButton, tableCell, tinyButton, usd, when } from './parts';

function useJobs(type: string) {
  return useQuery({
    queryKey: ['jobs', type],
    queryFn: () => api<{ jobs: JobDto[] }>(`/api/admin/jobs?type=${type}`),
    refetchInterval: (q) =>
      q.state.data?.jobs.some((j) => j.state === 'queued' || j.state === 'running') ? 1500 : false,
  });
}

function rerateStatus(job: JobDto | undefined): string {
  if (!job) return 'Never run';
  if (job.state === 'queued') return 'Queued';
  if (job.state === 'running') return `Running · ${Math.round(job.progress * 100)}%`;
  if (job.state === 'failed') return `Failed: ${job.error ?? 'unknown error'}`;
  const r = job.result as RerateResult | null;
  const at = when(job.finishedAt ?? job.createdAt);
  return r ? `Last run ${at} · ${r.checked} checked, ${r.changed} changed` : `Last run ${at}`;
}

export const SCHEDULE_LABELS: Record<string, string> = {
  hourly: 'Hourly',
  daily: 'Daily 04:00',
};

function backfillLine(b: OverviewResponse['backfills'][number]): string {
  if (b.state === 'idle') return 'Not started';
  if (b.state === 'done') return `Done · ${b.imported} puzzles`;
  if (b.state === 'paused') return `Paused · ${b.imported} puzzles so far`;
  return `${b.checked.toLocaleString('en-GB')} checked · ${b.imported} puzzles`;
}

/** Admin overview (mockup 08): what exists so far. The generator pool, nightly tests and
 * solver stalls join it in their stages. */
export function AdminOverviewPage() {
  const client = useQueryClient();
  const overview = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => api<OverviewResponse>('/api/admin/overview'),
    refetchInterval: 15_000,
  });
  const rerate = useJobs('rerate');
  const latest = rerate.data?.jobs[0];
  const busy = latest?.state === 'queued' || latest?.state === 'running';
  const startRerate = useMutation({
    mutationFn: () => api<{ jobId: string }>('/api/admin/rerate', { method: 'POST' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['jobs', 'rerate'] }),
  });
  const runNightly = useMutation({
    mutationFn: () => api<{ queued: number }>('/api/admin/run-nightly', { method: 'POST' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['admin'] }),
  });
  const toggle = useMutation({
    mutationFn: (v: { id: string; enabled: boolean }) =>
      api(`/api/admin/sources/${v.id}`, { method: 'PATCH', json: { enabled: v.enabled } }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['admin'] }),
  });
  const d = overview.data;
  const month = new Date().toLocaleDateString('en-GB', { month: 'long' });
  const now = new Date().toLocaleString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-1 flex-col gap-0.5">
          <h1 className="m-0 text-[26px] font-bold">Overview</h1>
          <span className="text-[13px] text-muted">{now.replace(' at ', ' · ')}</span>
        </div>
        <Link
          to="/admin/import"
          className="flex h-10 items-center rounded-btn border border-line bg-surface px-3.5 text-[14px] font-semibold text-ink no-underline"
        >
          Import puzzle
        </Link>
        <button
          type="button"
          disabled={runNightly.isPending}
          onClick={() => runNightly.mutate()}
          className="h-10 rounded-btn border-0 bg-ink px-3.5 text-[14px] font-semibold text-white disabled:opacity-50"
        >
          Run nightly jobs now
        </button>
      </div>
      {overview.isError && (
        <p role="alert" className="m-0 text-[14px] text-mistake">
          {overview.error.message}
        </p>
      )}
      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title={`API spend · ${month}`} small>
          <div className="flex items-baseline gap-1.5">
            <span className="text-[32px] font-bold tabular-nums">
              {usd(d?.costs.monthUsd ?? 0)}
            </span>
            <span className="text-[14px] text-muted">of {usd(d?.costs.capUsd ?? 0)} cap</span>
          </div>
          <Bar value={d ? d.costs.monthUsd / Math.max(d.costs.capUsd, 0.01) : 0} />
          <div className="flex flex-col gap-1 text-[13px]">
            {(d?.costs.byPurpose ?? []).map((p) => (
              <div key={p.purpose} className="flex justify-between">
                <span>
                  {p.purpose === 'rule-reading-batch' ? 'Rule reading (batch)' : 'Rule reading'} ·{' '}
                  {p.calls} puzzles
                </span>
                <span className="tabular-nums">{usd(p.usd)}</span>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Batch jobs" small>
          {(d?.backfills ?? []).map((b) => (
            <div key={b.name} className="flex flex-col gap-1.5">
              <div className="flex justify-between text-[14px]">
                <span className="font-semibold">{b.label}</span>
                <span className="text-muted tabular-nums">{backfillLine(b)}</span>
              </div>
              {b.state === 'running' && b.total ? <Bar value={b.checked / b.total} /> : null}
            </div>
          ))}
          <div className="flex justify-between gap-3 text-[14px]">
            <span className="font-semibold">Re-rate library</span>
            <span className="text-muted" data-testid="rerate-status">
              {rerateStatus(latest)}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy || startRerate.isPending}
              onClick={() => startRerate.mutate()}
              className={smallButton}
            >
              Re-rate now
            </button>
            <Link to="/admin/batch" className={smallButton}>
              All batch jobs
            </Link>
          </div>
        </Panel>
        <Panel title="Nightly tests" small>
          <NightlyLines n={d?.nightly ?? null} />
          <Link to="/admin/solver" className={`${tinyButton} flex w-fit items-center no-underline`}>
            Solver report
          </Link>
        </Panel>
      </div>
      <div className="grid gap-4 lg:grid-cols-[1.45fr_1fr]">
        <Panel
          title="Sources"
          side={
            <Link to="/admin/sources" className={smallButton}>
              Add source
            </Link>
          }
        >
          <div className="-mx-1 overflow-x-auto px-1">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="text-left text-muted">
                  <th className={`${tableCell} font-semibold`}>Source</th>
                  <th className={`${tableCell} font-semibold`}>Checked</th>
                  <th className={`${tableCell} font-semibold`}>Last run</th>
                  <th className={`${tableCell} text-right font-semibold`}>New</th>
                  <th className={`${tableCell} text-right font-semibold`}>On</th>
                </tr>
              </thead>
              <tbody>
                {(d?.sources ?? []).map((s) => (
                  <tr key={s.id}>
                    <td className={`${tableCell} font-semibold`}>{s.name}</td>
                    <td className={tableCell}>{SCHEDULE_LABELS[s.schedule] ?? s.schedule}</td>
                    <td
                      className={`${tableCell} ${s.lastError ? 'font-semibold text-[#A3212D]' : ''}`}
                    >
                      {s.lastError ? s.lastError.slice(0, 60) : when(s.lastRunAt)}
                    </td>
                    <td className={`${tableCell} text-right tabular-nums`}>{s.lastFound}</td>
                    <td className={`${tableCell} text-right`}>
                      <SmallSwitch
                        checked={s.enabled}
                        label={`${s.name} on`}
                        onChange={(enabled) => toggle.mutate({ id: s.id, enabled })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
        <Panel
          title="Generator pool"
          side={
            <Link to="/admin/pool" className="text-[14px] font-semibold">
              Browse pool
            </Link>
          }
        >
          {(d?.pool.categories ?? []).map((c) => (
            <div key={c.key} className="flex items-center gap-3 text-[14px]">
              <span className="w-[42%] shrink-0">{c.name}</span>
              <span className="flex-1">
                <Bar value={c.size ? c.count / c.size : 0} />
              </span>
              <span className="w-12 text-right tabular-nums">
                {c.count}/{c.size}
              </span>
            </div>
          ))}
          {d && (
            <span className="mt-auto pt-2 text-[13px] text-muted">
              Daily at {d.pool.dailyTime} · {d.pool.unplayed} unplayed · pauses at {d.pool.backlog}
            </span>
          )}
        </Panel>
      </div>
      <div className="grid gap-4 lg:grid-cols-[1.12fr_1fr]">
        <Panel
          title="Needs a plugin"
          side={
            <span className="text-[13px] text-muted">
              {d?.needsPlugin.total ?? 0} puzzles waiting
            </span>
          }
        >
          {(d?.needsPlugin.top ?? []).map((g) => (
            <div
              key={g.signature}
              className="flex items-center gap-2.5 border-b border-line-soft py-1.5 text-[14px]"
            >
              <span className="min-w-0 flex-1 truncate font-semibold" title={g.signature}>
                {g.signature}
              </span>
              <span className="text-muted">
                {g.count} {g.count === 1 ? 'puzzle' : 'puzzles'}
              </span>
            </div>
          ))}
          <Link
            to="/admin/needs-plugin"
            className={`${tinyButton} flex w-fit items-center no-underline`}
          >
            Copy requests
          </Link>
        </Panel>
        <Panel
          title="Where the solver stalls"
          side={<span className="text-[13px] text-muted">Worth adding as techniques</span>}
        >
          {(d?.stalls ?? []).length === 0 && (
            <span className="text-[14px] text-muted">
              {d?.nightly?.result
                ? 'Nowhere in the last nightly run.'
                : 'After the first nightly run.'}
            </span>
          )}
          {(d?.stalls ?? []).map((g, i, all) => (
            <div
              key={g.key}
              className={`flex items-center gap-2.5 py-1.5 text-[14px] ${i < all.length - 1 ? 'border-b border-line-soft' : ''}`}
            >
              <span className="min-w-0 flex-1">{g.label}</span>
              <span className="text-muted">
                {g.count} {g.count === 1 ? 'puzzle' : 'puzzles'}
              </span>
            </div>
          ))}
        </Panel>
      </div>
    </>
  );
}
